import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Drawer, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { getNotifications, closeNotice } from "./api";
import CheckIcon from "@material-ui/icons/Check";
import clsx from "clsx";
import { unicodeEscapesToEmoji } from "utils/emojis";
import ExpandableText from "../UI/ExpandableText";

const NotificationsDrawer = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const isUserAuthenticated = useSelector(
    (state) => state.appState.isUserAuthenticated
  );

  const handleClose = async (id) => {
    try {
      const res = await closeNotice(id);
      if (!res) return;
      setNotifications((prev) =>
        prev.filter((notification) => notification.notice_id !== id)
      );
      setOpen((prev) => prev && notifications.length > 1);
    } catch (error) {
      console.error("Ошибка при закрытии уведомлений:", error);
    }
  };

  const fetchNotifications = async () => {
    if (!isUserAuthenticated) return;
    try {
      const response = await getNotifications();
      
      if (response && response.length > 0) {  
        setNotifications(response);
        setOpen(true);
      }
    } catch (error) {
      console.error("Ошибка при получении уведомлений:", error);
    }
  };

  useEffect(() => {
    if (!isUserAuthenticated) return;
    // Первоначальная загрузка
    fetchNotifications();

    // Настройка интервала (каждые пол часа)
    const interval = setInterval(() => {
      fetchNotifications();
    }, 60000); // 60000 мс = 1 минута

    // Очистка интервала при размонтировании компонента
    return () => clearInterval(interval);
  }, [isUserAuthenticated]);

  return (
    <Drawer
      anchor="left"
      open={open}
      BackdropProps={{ invisible: true }}
      classes={{
        paper: classes.drawer,
      }}
    >
      <Box className={classes.sidebar}>
        <Box className={classes.header}>
          <Typography className={classes.title}>Новые уведомления</Typography>
        </Box>
        <Box className={classes.content}>
          {notifications.length > 0 ? (
            notifications.map((notification, index) => (
              <Box key={index}>
                <NotificationItem
                  notice_id={notification.notice_id}
                  title={unicodeEscapesToEmoji(notification.head_text)}
                  date={notification.date_create || new Date().toLocaleString()}
                  description={notification.fio}
                  action={unicodeEscapesToEmoji(notification.body_text)}
                  handleClose={handleClose}
                />
              </Box>
            ))
          ) : (
            <Typography className={classes.emptyMessage}>
              Нет новых уведомлений
            </Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

const NotificationItem = ({
  title,
  description,
  date,
  action,
  notice_id,
  handleClose,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const renderHtmlText = (text) => {
    if (!text) return null;
    const withBr = text.replace(/\r\n|\r|\n/g, "<br/>");
    return <span dangerouslySetInnerHTML={{ __html: withBr }} />;
  };

  return (
    <Box mb={1} className={classes.item}>
      <Typography className={classes.name}>
        {renderHtmlText(title)}
        <Box
          component="span"
          className={clsx([classes.date, { [classes.onlyDate]: !title }])}
        >
          {date}
        </Box>
      </Typography>
      {action && (
        <>
          <ExpandableText text={action} renderHtml={renderHtmlText} />
        </>
      )}
      {notice_id && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          mt={1}
        >
          <Box
            component="button"
            className={classes.acknowledgeButton}
            onClick={() => handleClose(notice_id)}
          >
            <Typography variant="body2" className={classes.buttonText}>
              Ознакомился
            </Typography>
            <CheckIcon fontSize="small" />
          </Box>
        </Box>
      )}
      {description && (
        <Typography className={classes.comment}>
          {renderHtmlText(description)}
        </Typography>
      )}
    </Box>
  );
};

const useStyles = makeStyles({
  drawer: {
    backgroundColor: "#F8FAFB",
    border: "1px solid #D2D3D9",
    boxShadow: "none",
    overflow: "hidden",
  },
  sidebar: {
    width: 500,
    height: "100vh",
    overflow: "auto",
  },
  header: {
    width: "100%",
    height: 60,
    display: "flex",
    alignItems: "center",
    backgroundColor: "#fff",
    borderBottom: "1px solid #D2D3D9",
    padding: "0 22px",
  },
  title: {
    width: "100%",
    textAlign: "center",
    fontSize: 16,
    fontWeight: 600,
    color: "#494C62",
  },
  icon: {
    marginLeft: "auto",
    color: "#6191EF",
    cursor: "pointer",
  },
  acknowledgeButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#6191EF",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    padding: "6px 12px",
    cursor: "pointer",
    transition: "background-color 0.3s",
    "&:hover": {
      backgroundColor: "#4A7AD9",
    },
    "& svg": {
      marginLeft: 6,
      fontSize: 16,
    },
  },
  buttonText: {
    fontSize: 12,
    fontWeight: 500,
    color: "#fff",
  },
  name: {
    fontSize: 12,
    fontWeight: 600,
    color: "#494C62",
  },
  date: {
    fontSize: 11,
    fontWeight: 400,
    color: "#9194A9",
    paddingLeft: 9,
  },
  onlyDate: {
    paddingLeft: 0,
  },
  action: {
    fontSize: 12,
    fontWeight: 400,
    color: "#000",
    wordBreak: "break-word",
  },
  comment: {
    fontSize: 12,
    fontWeight: 400,
    color: "#000",
    fontStyle: "italic",
  },
  content: {
    padding: "12px",
  },
  item: {
    borderBottom: "1px solid #afafaf",
    paddingBottom: "8px",
    marginBottom: "8px",
  },
  emptyMessage: {
    fontSize: 14,
    fontWeight: 400,
    color: "#9194A9",
    textAlign: "center",
    marginTop: 20,
  },
});

export default NotificationsDrawer;